import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import api from '../api';
import { errorText } from '../errors';
import SplitsTable from '../components/SplitsTable';
import RouteMap from '../components/RouteMap';
import Conditions from '../components/Conditions';

/* ── Read a stream's data array by type (streams = array of {stream_type, data}) ── */
function getStreamData(activity, streamType) {
  if (!activity || !activity.streams) return null;
  const s = activity.streams.find((st) => st.stream_type === streamType);
  return s && Array.isArray(s.data) && s.data.length > 0 ? s.data : null;
}

const chartTooltipStyle = {
  backgroundColor: '#111820',
  border: '1px solid #333',
  borderRadius: '6px',
  fontSize: '13px',
};

const backLink = {
  display: 'inline-block',
  marginBottom: '20px',
  color: '#93a1b1',
  fontSize: '14px',
};

const titleStyle = {
  fontSize: '22px',
  fontWeight: 700,
  color: '#ffffff',
  marginBottom: '8px',
  wordBreak: 'break-word',
};

const dateStyle = {
  fontSize: '14px',
  color: '#93a1b1',
  marginBottom: '24px',
};

const statsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
  gap: '12px',
  marginBottom: '24px',
};

const statCard = {
  backgroundColor: '#0b0f14',
  borderRadius: '8px',
  padding: '20px',
  textAlign: 'center',
};

const statLabel = {
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: '#93a1b1',
  marginBottom: '6px',
};

const statValue = {
  fontSize: '24px',
  fontWeight: 700,
  color: '#ff5a1f',
};

const statUnit = {
  fontSize: '13px',
  color: '#93a1b1',
  marginTop: '2px',
};

const sectionTitle = {
  fontSize: '20px',
  fontWeight: 600,
  color: '#ffffff',
  marginBottom: '16px',
  marginTop: '8px',
};

const loadingStyle = {
  textAlign: 'center',
  padding: '60px',
  color: '#93a1b1',
  fontSize: '16px',
};

const errorStyle = {
  padding: '16px',
  backgroundColor: '#3d1515',
  border: '1px solid #6b2020',
  borderRadius: '8px',
  color: '#ff4d4f',
};

const bestSplitCard = {
  backgroundColor: '#0b0f14',
  borderRadius: '8px',
  padding: '20px',
  textAlign: 'center',
  border: '1px solid #ff5a1f',
  marginBottom: '24px',
};

function formatDate(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(seconds) {
  if (!seconds) return '-';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function metersToKm(meters) {
  if (!meters) return '0.00';
  return (meters / 1000).toFixed(2);
}

function formatPace(movingTimeSeconds, distanceMeters) {
  if (!movingTimeSeconds || !distanceMeters) return '-';
  const km = distanceMeters / 1000;
  const paceSeconds = movingTimeSeconds / km;
  const mins = Math.floor(paceSeconds / 60);
  const secs = Math.round(paceSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatPaceSeconds(totalSeconds) {
  if (!totalSeconds || totalSeconds === Infinity) return '-';
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.round(totalSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDistanceLabel(meters) {
  if (meters >= 1000) return `${meters / 1000}km`;
  return `${meters}m`;
}

const deleteButtonStyle = {
  padding: '8px 20px',
  borderRadius: '6px',
  border: '1px solid #6b2020',
  backgroundColor: 'transparent',
  color: '#ff4d4f',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  marginLeft: '16px',
};

function formatDistLabel(m) {
  return m >= 1000 ? `${m / 1000}km` : `${m}m`;
}

/* ── Interval-focused stats from saved config ── */
function IntervalStats({ config }) {
  const result = config.result || {};
  const summary = result.summary || {};
  const totalWorkoutDist = (result.segments || [])
    .filter(s => s.type === 'rep')
    .reduce((sum, s) => sum + (s.distance_m || 0), 0);

  return (
    <div style={statsGrid}>
      <div style={statCard}>
        <div style={statLabel}>Reps</div>
        <div style={statValue}>{summary.total_reps || config.reps || '-'}</div>
        <div style={statUnit}>x {formatDistLabel(summary.rep_distance_m || config.distance || 0)}</div>
      </div>
      <div style={statCard}>
        <div style={statLabel}>Avg Rep Pace</div>
        <div style={statValue}>{formatPaceSeconds(summary.avg_rep_pace)}</div>
        <div style={statUnit}>min/km</div>
      </div>
      <div style={statCard}>
        <div style={statLabel}>Avg Rest</div>
        <div style={statValue}>{formatTime(summary.avg_rest_duration_s)}</div>
        <div style={statUnit}>duration</div>
      </div>
      <div style={statCard}>
        <div style={statLabel}>Workout Dist</div>
        <div style={statValue}>{totalWorkoutDist >= 1000 ? (totalWorkoutDist / 1000).toFixed(2) : totalWorkoutDist}</div>
        <div style={statUnit}>{totalWorkoutDist >= 1000 ? 'km' : 'm'}</div>
      </div>
    </div>
  );
}

/* ── Interval breakdown table (reusable for saved & live results) ── */
function IntervalBreakdown({ data }) {
  if (!data || !data.segments) return null;
  const summary = data.summary || {};
  return (
    <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
      <h2 style={{ ...sectionTitle, marginTop: 0, marginBottom: '14px' }}>Interval Breakdown</h2>
      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ backgroundColor: '#111820', borderRadius: '6px', padding: '10px 14px', textAlign: 'center', flex: '1 1 80px' }}>
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Workout</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#ff5a1f' }}>{summary.total_reps} x {summary.rep_distance_m}m</div>
        </div>
        <div style={{ backgroundColor: '#111820', borderRadius: '6px', padding: '10px 14px', textAlign: 'center', flex: '1 1 80px' }}>
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Avg Rep Pace</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#ff5a1f' }}>{formatPaceSeconds(summary.avg_rep_pace)}/km</div>
        </div>
        <div style={{ backgroundColor: '#111820', borderRadius: '6px', padding: '10px 14px', textAlign: 'center', flex: '1 1 80px' }}>
          <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Avg Rest</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#93a1b1' }}>{formatTime(summary.avg_rest_duration_s)}</div>
        </div>
      </div>
      {/* Fastest/Slowest */}
      {summary.fastest_rep && summary.total_reps > 1 && (
        <div style={{ fontSize: '12px', color: '#93a1b1', marginBottom: '14px' }}>
          Fastest: <span style={{ color: '#3ddc84', fontWeight: 600 }}>Rep {summary.fastest_rep} ({formatPaceSeconds(summary.fastest_rep_pace)}/km)</span>
          {summary.slowest_rep !== summary.fastest_rep && (
            <span style={{ marginLeft: '12px' }}>
              Slowest: <span style={{ color: '#ff4d4f', fontWeight: 600 }}>Rep {summary.slowest_rep} ({formatPaceSeconds(summary.slowest_rep_pace)}/km)</span>
            </span>
          )}
        </div>
      )}
      {/* Segments */}
      {data.segments.map((seg, i) => {
        const isRep = seg.type === 'rep';
        const isRest = seg.type === 'rest';
        const isFastest = isRep && seg.rep_number === summary.fastest_rep;
        const isSlowest = isRep && seg.rep_number === summary.slowest_rep && summary.total_reps > 1;
        const label = isRep ? `Rep ${seg.rep_number}`
          : isRest ? `Rest ${seg.rest_number}`
          : seg.type === 'warmup' ? 'Warmup' : 'Cooldown';
        return (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 12px', borderBottom: '1px solid #1e2936',
            backgroundColor: isFastest ? 'rgba(74,222,128,0.06)' : isSlowest ? 'rgba(255,107,107,0.06)' : 'transparent',
            fontSize: '13px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '80px' }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: isRep ? '#ff5a1f' : isRest ? '#555' : '#333',
              }} />
              <span style={{ color: isRep ? '#ff5a1f' : '#666', fontWeight: isRep ? 600 : 400 }}>{label}</span>
            </div>
            <span style={{ color: '#e8edf2' }}>{seg.distance_m}m</span>
            <span style={{ color: '#e8edf2' }}>{formatTime(seg.duration_s)}</span>
            <span style={{ color: isFastest ? '#3ddc84' : isSlowest ? '#ff4d4f' : isRep ? '#ff5a1f' : '#555', fontWeight: isRep ? 600 : 400 }}>
              {seg.pace_sec_per_km ? `${formatPaceSeconds(seg.pace_sec_per_km)}/km` : '-'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ActivityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [highlightDist, setHighlightDist] = useState(null);
  const [intervals, setIntervals] = useState(null);
  const [intervalsLoading, setIntervalsLoading] = useState(false);
  const [showIntervalForm, setShowIntervalForm] = useState(false);
  const [repCount, setRepCount] = useState(3);
  const [repDistance, setRepDistance] = useState(400);
  const [showTiming, setShowTiming] = useState(false);
  const [warmupSec, setWarmupSec] = useState(180);
  const [restSec, setRestSec] = useState(180);
  const [laps, setLaps] = useState(null);
  const [insight, setInsight] = useState(null);
  const [intervalInsight, setIntervalInsight] = useState(null);
  const [runMetrics, setRunMetrics] = useState(null);
  const [showMarkPrompt, setShowMarkPrompt] = useState(false);
  const [marking, setMarking] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const isInterval = activity?.is_interval || false;
  const savedConfig = activity?.interval_config || null;
  const hasSavedResult = savedConfig && savedConfig.result;

  const handleDelete = async () => {
    if (!window.confirm('Delete this activity from your local database? (This does NOT delete from Strava)')) return;
    setDeleting(true);
    try {
      await api.delete(`/activities/${id}`);
      navigate('/');
    } catch (err) {
      alert('Failed to delete: ' + errorText(err, err.message));
      setDeleting(false);
    }
  };

  const handleMarkInterval = async (intervalsData) => {
    setMarking(true);
    try {
      const body = {
        is_interval: true,
        interval_config: {
          reps: repCount,
          distance: repDistance,
          warmup: showTiming ? warmupSec : null,
          rest: showTiming ? restSec : null,
          result: intervalsData,
        },
      };
      await api.post(`/activities/${id}/mark-interval`, body);
      // Update local activity state
      setActivity(prev => ({
        ...prev,
        is_interval: true,
        interval_config: body.interval_config,
      }));
      setIntervals(null);
      setShowMarkPrompt(false);
      setShowIntervalForm(false);
    } catch (err) {
      alert('Failed to mark as interval: ' + errorText(err, err.message));
    }
    setMarking(false);
  };

  const handleUntagInterval = async () => {
    if (!window.confirm('Remove interval tag? This run will be included in pace averages again.')) return;
    try {
      await api.post(`/activities/${id}/mark-interval`, { is_interval: false, interval_config: null });
      setActivity(prev => ({
        ...prev,
        is_interval: false,
        interval_config: null,
      }));
      setIntervals(null);
      setShowIntervalForm(false);
    } catch (err) {
      alert('Failed to untag: ' + errorText(err, err.message));
    }
  };

  const handleAnalyze = async () => {
    setIntervalsLoading(true);
    try {
      let url = `/activities/${id}/intervals?reps=${repCount}&distance=${repDistance}`;
      if (showTiming) {
        url += `&warmup=${warmupSec}&rest=${restSec}`;
      }
      const res = await api.get(url);
      setIntervals(res.data);
      if (res.data.is_interval) {
        setShowMarkPrompt(true);
      }
    } catch {
      setIntervals({ is_interval: false, message: 'Failed to analyze intervals' });
    }
    setIntervalsLoading(false);
  };

  // The transcript lives on the server now, so it survives a refresh.
  useEffect(() => {
    if (!id) return;
    api.get(`/activities/${id}/chat`)
      .then((res) => setMessages((res.data && res.data.messages) || []))
      .catch(() => {});
  }, [id]);

  const sendChat = async (text) => {
    const content = (text || '').trim();
    if (!content || sending) return;
    setMessages((prev) => [...prev, { role: 'user', content }]);
    setChatInput('');
    setSending(true);
    try {
      const res = await api.post(`/activities/${id}/chat`, { message: content });
      const reply = res.data && res.data.reply
        ? res.data.reply
        : 'Sorry — something went wrong. Try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry — something went wrong. Try again.' }]);
    }
    setSending(false);
  };

  const clearChat = async () => {
    try {
      await api.delete(`/activities/${id}/chat`);
      setMessages([]);
    } catch { /* leave the transcript alone if the delete failed */ }
  };

  // Auto-scroll the messages area to the bottom on new messages / thinking state.
  useEffect(() => {
    if (chatOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, sending, chatOpen]);

  useEffect(() => {
    api
      .get(`/activities/${id}`)
      .then((res) => {
        setActivity(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(errorText(err, 'Failed to load activity'));
        setLoading(false);
      });

    api.get(`/activities/${id}/insights`)
      .then((res) => { if (res.data.narratives?.length) setInsight(res.data); })
      .catch(() => {});

    api.get(`/activities/${id}/metrics`)
      .then((res) => { if (res.data.consistency != null) setRunMetrics(res.data); })
      .catch(() => {});

    api.get(`/activities/${id}/interval-insights`)
      .then((res) => { if (res.data.narratives?.length) setIntervalInsight(res.data); })
      .catch(() => {});

    api.get(`/activities/${id}/laps`)
      .then((res) => { if (res.data.lap_count >= 2) setLaps(res.data); })
      .catch(() => {});

    api
      .get(`/activities/${id}/analysis`)
      .then((res) => {
        setAnalysis(res.data);
      })
      .catch(() => {
        // No analysis available (no GPS streams) - that's fine
      });
  }, [id]);

  const best1kmSplit = useMemo(() => {
    if (!activity || !activity.splits || activity.splits.length === 0) return null;
    let bestPace = Infinity;
    let bestSplit = null;
    activity.splits.forEach((split) => {
      const dist = split.distance || 0;
      const time = split.moving_time || split.elapsed_time || 0;
      if (dist > 0 && time > 0) {
        const km = dist / 1000;
        const paceSeconds = time / km;
        if (paceSeconds < bestPace) {
          bestPace = paceSeconds;
          bestSplit = { ...split, paceSeconds: paceSeconds };
        }
      }
    });
    return bestSplit;
  }, [activity]);

  if (loading) {
    return <div style={loadingStyle}>Loading activity...</div>;
  }

  if (error) {
    return (
      <div>
        <Link to="/" style={backLink}>
          &larr; Back to Dashboard
        </Link>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  if (!activity) return null;

  const elevationM = activity.total_elevation_gain
    ? Math.round(activity.total_elevation_gain)
    : 0;

  const savedRepDist = savedConfig?.distance || 0;
  const allBestEfforts = analysis?.best_efforts || [];
  const filteredBestEfforts = isInterval && savedRepDist
    ? allBestEfforts.filter(e => e.distance < savedRepDist)
    : allBestEfforts;
  const hasBestEfforts = filteredBestEfforts.length > 0;

  return (
    <div>
      <Link to="/" style={backLink}>
        &larr; Back to Dashboard
      </Link>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <h1 style={{ ...titleStyle, marginBottom: 0, flex: '1 1 auto', minWidth: 0 }}>{activity.name || 'Untitled Activity'}</h1>
        <button style={{ ...deleteButtonStyle, marginLeft: 0, whiteSpace: 'nowrap', flexShrink: 0 }} onClick={handleDelete} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
      <div style={dateStyle}>
        {formatDate(activity.start_date)}
        {activity.route_name && (
          <span style={{ marginLeft: '12px', color: '#ff5a1f', backgroundColor: '#ff5a1f15', padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
            {activity.route_name}
          </span>
        )}
        {isInterval && (
          <span style={{ marginLeft: '8px', color: '#f5a623', backgroundColor: '#f5a62315', padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
            Interval
          </span>
        )}
      </div>

      {/* ── What the weather cost this run ── */}
      <Conditions activity={activity} />

      {/* ── Chat about this run ── */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => setChatOpen((o) => !o)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            minHeight: '44px', padding: '0 20px', borderRadius: '8px', border: 'none',
            backgroundColor: '#ff5a1f', color: '#fff', fontSize: '15px', fontWeight: 600,
            cursor: 'pointer',
          }}>
          <span>&#128172;</span>
          <span>{chatOpen ? 'Hide chat' : 'Chat about this run'}</span>
          {!chatOpen && messages.length > 0 && (
            <span style={{
              backgroundColor: '#ffffff33', borderRadius: '10px', padding: '1px 7px',
              fontSize: '12px', fontWeight: 700,
            }}>
              {messages.length}
            </span>
          )}
        </button>

        {chatOpen && messages.length > 0 && (
          <button
            onClick={clearChat}
            style={{
              marginLeft: '12px', minHeight: '44px', background: 'none', border: 'none',
              color: '#93a1b1', fontSize: '13px', cursor: 'pointer',
            }}>
            Clear chat
          </button>
        )}

        {chatOpen && (
          <div style={{
            marginTop: '12px', backgroundColor: '#0b0f14', borderRadius: '8px',
            border: '1px solid #1e2936', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Messages area */}
            <div ref={messagesEndRef} style={{
              maxHeight: '380px', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px',
            }}>
              {messages.length === 0 && !sending && (
                <div>
                  <div style={{ fontSize: '13px', color: '#93a1b1', lineHeight: 1.6, marginBottom: '12px' }}>
                    Ask me anything about this run — pacing, heart rate, how it stacks up against your recent runs.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      'How does this run compare to my recent runs?',
                      'Was my pace even, or did I fade?',
                      'Was my heart rate too high for an easy run?',
                      'How am I trending lately?',
                    ].map((chip) => (
                      <button
                        key={chip}
                        onClick={() => sendChat(chip)}
                        style={{
                          textAlign: 'left', minHeight: '44px', padding: '8px 14px',
                          borderRadius: '8px', border: '1px solid #ff5a1f44',
                          backgroundColor: '#ff5a1f12', color: '#ff5a1f',
                          fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                        }}>
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => {
                const isUser = m.role === 'user';
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '85%', padding: '10px 14px', borderRadius: '12px',
                      fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      backgroundColor: isUser ? '#ff5a1f' : '#111820',
                      color: isUser ? '#fff' : '#e8edf2',
                      borderTopRightRadius: isUser ? '4px' : '12px',
                      borderTopLeftRadius: isUser ? '12px' : '4px',
                    }}>
                      {m.content}
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{
                    padding: '10px 14px', borderRadius: '12px', borderTopLeftRadius: '4px',
                    fontSize: '14px', color: '#93a1b1', backgroundColor: '#111820', fontStyle: 'italic',
                  }}>
                    Analyzing your run&hellip;
                  </div>
                </div>
              )}
            </div>

            {/* Input row */}
            <div style={{
              display: 'flex', gap: '8px', padding: '12px 16px',
              borderTop: '1px solid #1e2936',
            }}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendChat(chatInput); } }}
                placeholder="Ask about this run…"
                style={{
                  flex: 1, minWidth: 0, minHeight: '44px', padding: '0 14px', borderRadius: '8px',
                  border: '1px solid #333', backgroundColor: '#0b0f14', color: '#fff',
                  fontSize: '16px',
                }}
              />
              <button
                onClick={() => sendChat(chatInput)}
                disabled={sending || !chatInput.trim()}
                style={{
                  minHeight: '44px', padding: '0 20px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#ff5a1f', color: '#fff', fontSize: '14px', fontWeight: 600,
                  cursor: (sending || !chatInput.trim()) ? 'default' : 'pointer',
                  opacity: (sending || !chatInput.trim()) ? 0.5 : 1,
                  flexShrink: 0,
                }}>
                Send
              </button>
            </div>
          </div>
        )}
      </div>

      {activity.gps_glitch_count > 0 && (
        <div style={{
          backgroundColor: '#3d2515', border: '1px solid #6b4020', borderRadius: '8px',
          padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#f5a623',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <span style={{ fontSize: '16px' }}>&#9888;</span>
          <span>GPS signal issues detected ({activity.gps_glitch_count} glitch{activity.gps_glitch_count > 1 ? 'es' : ''}). Some best effort times may have been filtered to ensure accuracy.</span>
        </div>
      )}

      {/* ── Stats section ── */}
      {isInterval && hasSavedResult ? (
        /* Case 2: Interval-focused stats */
        <IntervalStats config={savedConfig} />
      ) : (
        /* Regular stats */
        <div style={statsGrid}>
          <div style={statCard}>
            <div style={statLabel}>Distance</div>
            <div style={statValue}>{metersToKm(activity.distance)}</div>
            <div style={statUnit}>km</div>
          </div>
          <div style={statCard}>
            <div style={statLabel}>Pace</div>
            <div style={statValue}>{formatPace(activity.moving_time, activity.distance)}</div>
            <div style={statUnit}>min/km</div>
          </div>
          <div style={statCard}>
            <div style={statLabel}>Moving Time</div>
            <div style={statValue}>{formatTime(activity.moving_time)}</div>
            <div style={statUnit}>h:m:s</div>
          </div>
          <div style={statCard}>
            <div style={statLabel}>Elapsed Time</div>
            <div style={statValue}>{formatTime(activity.elapsed_time)}</div>
            <div style={statUnit}>h:m:s</div>
          </div>
          <div style={statCard}>
            <div style={statLabel}>Elevation Gain</div>
            <div style={statValue}>{elevationM}</div>
            <div style={statUnit}>m</div>
          </div>
          {activity.average_heartrate && (
            <div style={statCard}>
              <div style={statLabel}>Avg Heart Rate</div>
              <div style={statValue}>{Math.round(activity.average_heartrate)}</div>
              <div style={statUnit}>bpm</div>
            </div>
          )}
          {activity.max_heartrate && (
            <div style={statCard}>
              <div style={statLabel}>Max Heart Rate</div>
              <div style={statValue}>{Math.round(activity.max_heartrate)}</div>
              <div style={statUnit}>bpm</div>
            </div>
          )}
          {activity.average_cadence && (
            <div style={statCard}>
              <div style={statLabel}>Avg Cadence</div>
              {/* A run's average is stored in steps per minute for every source */}
              <div style={statValue}>{Math.round(activity.average_cadence)}</div>
              <div style={statUnit}>spm</div>
            </div>
          )}
        </div>
      )}

      {/* ── Interval Insight ── */}
      {isInterval && intervalInsight && intervalInsight.narratives.length > 0 && (
        <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <h2 style={{ ...sectionTitle, marginBottom: '12px', fontSize: '16px' }}>Interval Insight</h2>
          {intervalInsight.narratives.map((n, i) => (
            <p key={i} style={{ color: '#e8edf2', fontSize: '13px', lineHeight: 1.6, marginBottom: '6px' }}>{n}</p>
          ))}
          {intervalInsight.tips.length > 0 && (
            <div style={{ marginTop: '12px', borderTop: '1px solid #1e2936', paddingTop: '10px' }}>
              {intervalInsight.tips.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', fontSize: '12px' }}>
                  <span style={{ color: '#f5a623', flexShrink: 0 }}>Tip:</span>
                  <span style={{ color: '#93a1b1' }}>{t}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Run Insight — hidden for interval runs ── */}
      {!isInterval && insight && insight.narratives.length > 0 && (
        <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <h2 style={{ ...sectionTitle, marginBottom: '12px', fontSize: '16px' }}>Run Insight</h2>
          {insight.narratives.map((n, i) => (
            <p key={i} style={{ color: '#e8edf2', fontSize: '13px', lineHeight: 1.6, marginBottom: '6px' }}>{n}</p>
          ))}
          {/* Pacing flow with history comparison */}
          {insight.pace_segments && insight.pace_segments.length > 0 && (
            <div>
              <div style={{ display: 'flex', gap: '4px', margin: '14px 0 6px' }}>
                {insight.pace_segments.map((seg, i) => {
                  const allPaces = insight.pace_segments.map(s => s.pace);
                  const allHistPaces = insight.pace_segments.filter(s => s.history_avg).map(s => s.history_avg);
                  const allValues = [...allPaces, ...allHistPaces];
                  const minPace = Math.min(...allValues);
                  const maxPace = Math.max(...allValues);
                  const range = maxPace - minPace || 1;
                  const height = 24 + ((maxPace - seg.pace) / range) * 30;
                  const histHeight = seg.history_avg ? 24 + ((maxPace - seg.history_avg) / range) * 30 : 0;
                  const isFaster = seg.history_avg && seg.pace < seg.history_avg - 3;
                  const isSlower = seg.history_avg && seg.pace > seg.history_avg + 3;
                  return (
                    <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: isFaster ? '#3ddc84' : isSlower ? '#ff4d4f' : '#93a1b1', fontWeight: 600, marginBottom: '3px' }}>
                        {seg.pace_formatted}
                      </div>
                      <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', alignItems: 'flex-end', height: '56px' }}>
                        <div style={{ width: '40%', height: `${height}px`, borderRadius: '2px 2px 0 0', backgroundColor: isFaster ? '#3ddc84' : isSlower ? '#ff4d4f55' : '#ff5a1f77' }} />
                        {seg.history_avg && (
                          <div style={{ width: '40%', height: `${histHeight}px`, borderRadius: '2px 2px 0 0', backgroundColor: '#ffffff15', border: '1px solid #ffffff22' }} />
                        )}
                      </div>
                      <div style={{ fontSize: '9px', color: '#555', marginTop: '3px' }}>{seg.label}</div>
                    </div>
                  );
                })}
              </div>
              {insight.pace_segments.some(s => s.history_avg) && (
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', fontSize: '10px', color: '#555', marginBottom: '4px' }}>
                  <span><span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#ff5a1f77', borderRadius: '2px', marginRight: '4px' }}/>Today</span>
                  <span><span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#ffffff15', border: '1px solid #ffffff22', borderRadius: '2px', marginRight: '4px' }}/>Recent avg</span>
                </div>
              )}
            </div>
          )}
          {insight.tips.length > 0 && (
            <div style={{ marginTop: '12px', borderTop: '1px solid #1e2936', paddingTop: '10px' }}>
              {insight.tips.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', fontSize: '12px' }}>
                  <span style={{ color: '#f5a623', flexShrink: 0 }}>Tip:</span>
                  <span style={{ color: '#93a1b1' }}>{t}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Case 2: Saved interval breakdown ── */}
      {isInterval && hasSavedResult && (
        <>
          <IntervalBreakdown data={savedConfig.result} />
          {/* Interval metrics */}
          {runMetrics && runMetrics.consistency != null && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', marginTop: '-12px', fontSize: '12px', color: '#93a1b1', flexWrap: 'wrap', paddingLeft: '4px' }}>
              <span>Consistency: <strong style={{ color: runMetrics.consistency >= 80 ? '#3ddc84' : runMetrics.consistency >= 60 ? '#f5a623' : '#ff4d4f' }}>{runMetrics.consistency}/100</strong></span>
              <span>Fade: <strong style={{ color: Math.abs(runMetrics.fade_s) <= 5 ? '#3ddc84' : '#f5a623' }}>{runMetrics.fade_s > 0 ? '+' : ''}{runMetrics.fade_s}s</strong></span>
              <span>Decay: <strong style={{ color: Math.abs(runMetrics.decay_s_per_rep || 0) <= 2 ? '#3ddc84' : '#f5a623' }}>{(runMetrics.decay_s_per_rep || 0).toFixed(1)}s/rep</strong></span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '24px', marginTop: '-8px' }}>
            <button onClick={() => { setIntervals(null); setShowIntervalForm(true); }}
              style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #333', backgroundColor: 'transparent', color: '#93a1b1', fontSize: '11px', cursor: 'pointer' }}>
              Re-analyze
            </button>
            <button onClick={handleUntagInterval}
              style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #333', backgroundColor: 'transparent', color: '#666', fontSize: '11px', cursor: 'pointer' }}>
              Untag interval
            </button>
          </div>
        </>
      )}

      {/* ── Case 3: Tagged as interval but no saved config ── */}
      {isInterval && !hasSavedResult && (
        <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ color: '#f5a623', fontSize: '14px' }}>Tagged as interval run — excluded from pace averages</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {!showIntervalForm && (
                <button onClick={() => setShowIntervalForm(true)}
                  style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#ff5a1f', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                  Analyze reps
                </button>
              )}
              <button onClick={handleUntagInterval}
                style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #333', backgroundColor: 'transparent', color: '#666', fontSize: '12px', cursor: 'pointer' }}>
                Untag
              </button>
            </div>
          </div>
          {/* Inline form for Case 3 */}
          {showIntervalForm && !intervals && (
            <div style={{ marginTop: '16px' }}>
              <IntervalForm
                repCount={repCount} setRepCount={setRepCount}
                repDistance={repDistance} setRepDistance={setRepDistance}
                intervalsLoading={intervalsLoading}
                onAnalyze={handleAnalyze}
                onCancel={() => setShowIntervalForm(false)}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Case 1: Not interval, no saved config — prompt ── */}
      {!isInterval && activity.has_detailed_data && !intervals && !showIntervalForm && (
        <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ color: '#93a1b1', fontSize: '14px' }}>Was this an interval run?</span>
            <button onClick={() => setShowIntervalForm(true)}
              style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#ff5a1f', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
              Yes, analyze
            </button>
          </div>
        </div>
      )}

      {/* ── Interval config form (Case 1 expanded or re-analyze) ── */}
      {showIntervalForm && !intervals && (
        <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <IntervalForm
            repCount={repCount} setRepCount={setRepCount}
            repDistance={repDistance} setRepDistance={setRepDistance}
            intervalsLoading={intervalsLoading}
            onAnalyze={handleAnalyze}
            showTiming={showTiming} setShowTiming={setShowTiming}
            warmupSec={warmupSec} setWarmupSec={setWarmupSec}
            restSec={restSec} setRestSec={setRestSec}
            onCancel={() => setShowIntervalForm(false)}
          />
        </div>
      )}

      {/* ── Live interval results (not yet saved) ── */}
      {intervals && intervals.is_interval && (
        <>
          <IntervalBreakdown data={intervals} />
          {/* Mark as interval prompt */}
          {showMarkPrompt && (
            <div style={{
              backgroundColor: '#111820', borderRadius: '8px', padding: '16px', marginBottom: '24px', marginTop: '-16px',
              border: '1px solid #f5a62333',
            }}>
              <div style={{ fontSize: '14px', color: '#fff', fontWeight: 600, marginBottom: '6px' }}>Mark as interval run?</div>
              <div style={{ fontSize: '12px', color: '#93a1b1', marginBottom: '14px' }}>
                This will exclude this run from pace averages and save your interval analysis.
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={() => handleMarkInterval(intervals)} disabled={marking}
                  style={{
                    padding: '8px 18px', borderRadius: '6px', border: 'none',
                    backgroundColor: '#f5a623', color: '#0b0f14', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                    opacity: marking ? 0.6 : 1,
                  }}>
                  {marking ? 'Saving...' : 'Mark as Interval'}
                </button>
                <button onClick={() => { setShowMarkPrompt(false); }}
                  style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #333', backgroundColor: 'transparent', color: '#93a1b1', fontSize: '13px', cursor: 'pointer' }}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {/* Close button if prompt dismissed */}
          {!showMarkPrompt && (
            <div style={{ textAlign: 'right', marginBottom: '24px', marginTop: '-16px' }}>
              <button onClick={() => { setIntervals(null); setShowIntervalForm(false); }}
                style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #333', backgroundColor: 'transparent', color: '#666', fontSize: '11px', cursor: 'pointer' }}>
                Close results
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Interval analysis for Case 3 live results ── */}
      {isInterval && !hasSavedResult && intervals && intervals.is_interval && (
        <div style={{ textAlign: 'center', marginBottom: '24px', marginTop: '-16px' }}>
          <button onClick={() => handleMarkInterval(intervals)} disabled={marking}
            style={{
              padding: '8px 18px', borderRadius: '6px', border: 'none',
              backgroundColor: '#f5a623', color: '#0b0f14', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              opacity: marking ? 0.6 : 1,
            }}>
            {marking ? 'Saving...' : 'Save interval analysis'}
          </button>
        </div>
      )}

      {/* ── Interval not detected ── */}
      {intervals && !intervals.is_interval && (
        <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#666', fontSize: '13px' }}>{intervals.message}</span>
          <button onClick={() => { setIntervals(null); setShowIntervalForm(true); }}
            style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #333', backgroundColor: 'transparent', color: '#93a1b1', fontSize: '11px', cursor: 'pointer' }}>
            Try again
          </button>
        </div>
      )}

      {/* ── Best 1km split — hidden for interval runs ── */}
      {!isInterval && best1kmSplit && (
        <div style={bestSplitCard}>
          <div style={statLabel}>Best 1km Split</div>
          <div style={statValue}>{formatPaceSeconds(best1kmSplit.paceSeconds)}</div>
          <div style={statUnit}>
            min/km (Split #{best1kmSplit.split_number || best1kmSplit.split || '?'})
          </div>
        </div>
      )}

      {/* ── Run Analysis - Best Efforts ── */}
      {hasBestEfforts && (
        <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
          <h2 style={{ ...sectionTitle, marginTop: 0 }}>
            {isInterval ? `Best Efforts (under ${savedRepDist >= 1000 ? `${savedRepDist/1000}km` : `${savedRepDist}m`})` : 'Run Analysis'}
          </h2>

          {!isInterval && analysis.pace_percentile != null && (
            <div style={{
              backgroundColor: '#111820',
              borderRadius: '8px',
              padding: '14px 18px',
              marginBottom: '18px',
              border: '1px solid #ff5a1f33',
              fontSize: '15px',
              color: '#e8edf2',
              textAlign: 'center',
            }}>
              This run was faster than <span style={{ color: '#ff5a1f', fontWeight: 700 }}>{Math.round(analysis.pace_percentile)}%</span> of all your runs
            </div>
          )}

          <div style={{ fontSize: '14px', fontWeight: 600, color: '#93a1b1', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Best Efforts
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr 1fr 1fr 80px 80px',
              gap: '6px',
              padding: '8px 10px',
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: '#666',
              borderBottom: '1px solid #1e2936',
              minWidth: '480px',
            }}>
              <div>Dist</div>
              <div>Time</div>
              <div>Pace</div>
              <div>Percentile</div>
              <div>vs Best</div>
              <div>vs Phase</div>
            </div>

            {filteredBestEfforts.map((effort) => (
              <div key={effort.distance} style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 1fr 1fr 80px 80px',
                gap: '6px',
                padding: '8px 10px',
                alignItems: 'center',
                borderBottom: '1px solid #1e1e35',
                fontSize: '13px',
                minWidth: '480px',
              }}>
                <div>
                  <span style={{ color: '#e8edf2', fontWeight: 600 }}>{formatDistanceLabel(effort.distance)}</span>
                  <span style={{
                    marginLeft: '4px', fontSize: '8px', fontWeight: 600, padding: '1px 5px', borderRadius: '3px', verticalAlign: 'middle',
                    backgroundColor: effort.is_dedicated ? '#3ddc8415' : '#ff5a1f15',
                    color: effort.is_dedicated ? '#3ddc84' : '#ff5a1f',
                  }}>
                    {effort.is_dedicated ? 'Sprint' : 'Seg'}
                  </span>
                </div>
                <div style={{ color: '#e8edf2' }}>
                  {formatTime(effort.time_seconds)}
                </div>
                <div style={{ color: '#93a1b1', fontSize: '12px' }}>
                  {formatPaceSeconds(effort.pace_sec_per_km)}/km
                </div>
                <div style={{ color: '#93a1b1', fontSize: '11px' }}>
                  Top {100 - Math.round(effort.percentile)}%
                </div>
                <div>
                  {effort.is_pr ? (
                    <span style={{ backgroundColor: '#3ddc84', color: '#0a2a0a', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px' }}>PR!</span>
                  ) : effort.diff_from_best != null ? (
                    <span style={{ color: '#ff4d4f', fontSize: '12px' }}>+{Math.round(effort.diff_from_best)}s</span>
                  ) : '-'}
                </div>
                <div>
                  {effort.diff_from_phase != null ? (
                    effort.diff_from_phase <= 0 ? (
                      <span style={{ color: '#3ddc84', fontSize: '10px', fontWeight: 700 }}>Phase best!</span>
                    ) : (
                      <span style={{ color: effort.diff_from_phase <= 5 ? '#f5a623' : '#ff4d4f', fontSize: '12px' }}>+{Math.round(effort.diff_from_phase)}s</span>
                    )
                  ) : <span style={{ color: '#555', fontSize: '11px' }}>1st in phase</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
        <h2 style={{ ...sectionTitle, marginBottom: 0 }}>Route Map</h2>
        {(() => {
          const mapEfforts = isInterval && savedRepDist
            ? (activity.best_efforts || []).filter(be => be.distance_target < savedRepDist)
            : (activity.best_efforts || []);
          return mapEfforts.length > 0 && (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666', marginRight: '4px' }}>Fastest:</span>
            <button
              onClick={() => setHighlightDist(null)}
              style={{
                padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', border: 'none',
                backgroundColor: highlightDist === null ? '#ff5a1f' : '#111820',
                color: highlightDist === null ? '#fff' : '#93a1b1',
              }}>
              Full
            </button>
            {mapEfforts.map(be => (
              <button
                key={be.distance_target}
                onClick={() => setHighlightDist(highlightDist === be.distance_target ? null : be.distance_target)}
                style={{
                  padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', border: 'none',
                  backgroundColor: highlightDist === be.distance_target ? '#3ddc84' : '#111820',
                  color: highlightDist === be.distance_target ? '#0a2a0a' : '#93a1b1',
                }}>
                {formatDistLabel(be.distance_target)}
              </button>
            ))}
          </div>
        );})()}
      </div>
      <div style={{ marginBottom: '32px' }}>
        <RouteMap
          latlng={activity.streams?.find(s => s.stream_type === 'latlng')?.data}
          polyline={activity.map_summary_polyline}
          height={350}
          highlight={highlightDist && activity.best_efforts ? (() => {
            const be = activity.best_efforts.find(e => e.distance_target === highlightDist);
            return be && be.start_index != null ? { startIndex: be.start_index, endIndex: be.end_index } : null;
          })() : null}
        />
        {highlightDist && activity.best_efforts && (() => {
          const be = activity.best_efforts.find(e => e.distance_target === highlightDist);
          if (!be) return null;
          return (
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#93a1b1', textAlign: 'center' }}>
              Fastest {formatDistLabel(highlightDist)}: <span style={{ color: '#3ddc84', fontWeight: 600 }}>{formatTime(be.time_seconds)}</span>
              <span style={{ marginLeft: '8px', color: '#666' }}>({formatPaceSeconds(be.pace_sec_per_km)}/km)</span>
            </div>
          );
        })()}
      </div>

      {/* ── Heart Rate (Garmin only — guarded on heartrate stream) ── */}
      {(() => {
        const hrStream = getStreamData(activity, 'heartrate');
        if (!hrStream) return null;
        const zones = Array.isArray(activity.hr_zones) ? activity.hr_zones : [];
        const maxZoneSecs = zones.reduce((m, z) => Math.max(m, z.secs || 0), 0);
        return (
          <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              <h2 style={{ ...sectionTitle, marginBottom: 0, marginTop: 0 }}>Heart Rate</h2>
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#93a1b1' }}>
                {typeof activity.average_heartrate === 'number' && (
                  <span>Avg <strong style={{ color: '#ff5a1f' }}>{Math.round(activity.average_heartrate)}</strong> bpm</span>
                )}
                {typeof activity.max_heartrate === 'number' && (
                  <span>Max <strong style={{ color: '#ff4d4f' }}>{Math.round(activity.max_heartrate)}</strong> bpm</span>
                )}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={hrStream.map((v, i) => ({ i, bpm: v }))} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2936" />
                <XAxis dataKey="i" tick={false} height={4} />
                <YAxis tick={{ fill: '#666', fontSize: 10 }} domain={['auto', 'auto']} width={35} />
                <Tooltip contentStyle={chartTooltipStyle} labelFormatter={() => ''} formatter={(v) => [`${Math.round(v)} bpm`, 'HR']} />
                <Line type="monotone" dataKey="bpm" name="HR" stroke="#ff4d4f" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            {zones.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#93a1b1', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Time in Zones
                </div>
                {zones.map((z, i) => {
                  const secs = z.secs || 0;
                  const pct = maxZoneSecs > 0 ? (secs / maxZoneSecs) * 100 : 0;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '12px' }}>
                      <span style={{ width: '70px', color: '#93a1b1', flexShrink: 0 }}>
                        Z{z.zone}{z.low_bpm != null ? ` (${z.low_bpm}+)` : ''}
                      </span>
                      <div style={{ flex: 1, backgroundColor: '#111820', borderRadius: '4px', height: '16px', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', backgroundColor: '#ff5a1f', borderRadius: '4px' }} />
                      </div>
                      <span style={{ width: '54px', textAlign: 'right', color: '#e8edf2', flexShrink: 0 }}>{formatTime(secs)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Cadence (guarded on cadence stream; samples count one foot, so ×2 = spm) ── */}
      {(() => {
        const cadenceStream = getStreamData(activity, 'cadence');
        if (!cadenceStream) return null;
        return (
          <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              <h2 style={{ ...sectionTitle, marginBottom: 0, marginTop: 0 }}>Cadence</h2>
              {typeof activity.average_cadence === 'number' && (
                <div style={{ fontSize: '13px', color: '#93a1b1' }}>
                  Avg <strong style={{ color: '#60a5fa' }}>{Math.round(activity.average_cadence)}</strong> spm
                </div>
              )}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={cadenceStream.map((v, i) => ({ i, spm: v == null ? null : v * 2 }))} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2936" />
                <XAxis dataKey="i" tick={false} height={4} />
                <YAxis tick={{ fill: '#666', fontSize: 10 }} domain={['auto', 'auto']} width={35} />
                <Tooltip contentStyle={chartTooltipStyle} labelFormatter={() => ''} formatter={(v) => [`${Math.round(v)} spm`, 'Cadence']} />
                <Line type="monotone" dataKey="spm" name="Cadence" stroke="#60a5fa" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      })()}

      {/* ── Running Dynamics (Garmin only — guarded on non-null object w/ a value) ── */}
      {(() => {
        const rd = activity.running_dynamics;
        if (!rd || typeof rd !== 'object') return null;
        const hasAny = rd.stride_length != null || rd.ground_contact_time != null || rd.vertical_oscillation != null;
        if (!hasAny) return null;
        return (
          <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
            <h2 style={{ ...sectionTitle, marginTop: 0 }}>Running Dynamics</h2>
            <div style={statsGrid}>
              {rd.stride_length != null && (
                <div style={statCard}>
                  <div style={statLabel}>Stride Length</div>
                  {/* Garmin stores stride length in cm; show meters */}
                  <div style={statValue}>{(rd.stride_length / 100).toFixed(2)}</div>
                  <div style={statUnit}>m</div>
                </div>
              )}
              {rd.ground_contact_time != null && (
                <div style={statCard}>
                  <div style={statLabel}>Ground Contact</div>
                  <div style={statValue}>{rd.ground_contact_time}</div>
                  <div style={statUnit}>ms</div>
                </div>
              )}
              {rd.vertical_oscillation != null && (
                <div style={statCard}>
                  <div style={statLabel}>Vertical Oscillation</div>
                  <div style={statValue}>{rd.vertical_oscillation}</div>
                  <div style={statUnit}>cm</div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Laps — always shown */}
      {!isInterval && laps && laps.lap_count >= 2 && (
        <div style={{ backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ ...sectionTitle, marginBottom: 0 }}>
              Laps <span style={{ fontSize: '13px', color: '#666', fontWeight: 400, marginLeft: '8px' }}>~{laps.avg_lap_distance_m}m loop</span>
            </h2>
            <div style={{ fontSize: '12px', color: '#93a1b1' }}>
              Avg: <span style={{ color: '#ff5a1f', fontWeight: 600 }}>{formatTime(laps.stats.avg_lap_time)}</span>
              <span style={{ color: '#555', margin: '0 4px' }}>|</span>
              {formatPaceSeconds(laps.stats.avg_pace)}/km
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {laps.laps.map((lap) => {
              const isFastest = lap.lap_number === laps.stats.fastest_lap;
              const isSlowest = lap.lap_number === laps.stats.slowest_lap;
              const diff = lap.duration_s - laps.stats.fastest_time;
              return (
                <div key={lap.lap_number} style={{
                  backgroundColor: isFastest ? '#3ddc8412' : isSlowest ? '#ff4d4f08' : '#111820',
                  border: isFastest ? '1px solid #3ddc8433' : isSlowest ? '1px solid #ff4d4f22' : '1px solid #1e2936',
                  borderRadius: '6px', padding: '8px 12px', textAlign: 'center',
                  minWidth: '70px', flex: '0 0 auto',
                }}>
                  <div style={{ fontSize: '10px', color: '#666' }}>Lap {lap.lap_number}</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: isFastest ? '#3ddc84' : isSlowest ? '#ff4d4f' : '#e8edf2' }}>
                    {formatTime(lap.duration_s)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#555' }}>
                    {diff === 0 ? 'Best' : `+${diff}s`}
                  </div>
                </div>
              );
            })}
            {laps.partial_lap && (
              <div style={{ backgroundColor: '#111820', border: '1px solid #1e2936', borderRadius: '6px', padding: '8px 12px', textAlign: 'center', minWidth: '70px', opacity: 0.5 }}>
                <div style={{ fontSize: '10px', color: '#666' }}>Partial</div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#666' }}>{formatTime(laps.partial_lap.duration_s)}</div>
                <div style={{ fontSize: '10px', color: '#555' }}>{laps.partial_lap.distance_m}m</div>
              </div>
            )}
          </div>
          {/* Inline metrics for regular runs */}
          {runMetrics && runMetrics.consistency != null && (
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px', fontSize: '12px', color: '#93a1b1', flexWrap: 'wrap' }}>
              <span>Consistency: <strong style={{ color: runMetrics.consistency >= 80 ? '#3ddc84' : runMetrics.consistency >= 60 ? '#f5a623' : '#ff4d4f' }}>{runMetrics.consistency}/100</strong></span>
              <span>Fade: <strong style={{ color: Math.abs(runMetrics.fade_s) <= 5 ? '#3ddc84' : '#f5a623' }}>{runMetrics.fade_s > 0 ? '+' : ''}{runMetrics.fade_s}s</strong></span>
              <span>Decay: <strong style={{ color: Math.abs(runMetrics.decay_s_per_lap || 0) <= 2 ? '#3ddc84' : '#f5a623' }}>{(runMetrics.decay_s_per_lap || 0).toFixed(1)}s/lap</strong></span>
            </div>
          )}
        </div>
      )}

      {/* Splits — hidden for interval runs */}
      {!isInterval && activity.splits && activity.splits.length > 0 && (
        <>
          <h2 style={sectionTitle}>Splits</h2>
          <SplitsTable splits={activity.splits} />
        </>
      )}
    </div>
  );
}

/* ── Custom distance input with local string state ── */
const PRESET_DISTANCES = [100, 200, 250, 400, 500, 800, 1000];

function CustomDistanceInput({ repDistance, setRepDistance }) {
  const isCustom = !PRESET_DISTANCES.includes(repDistance);
  const [text, setText] = useState(isCustom ? String(repDistance) : '');

  // Reset input when a preset is clicked externally
  useEffect(() => {
    if (PRESET_DISTANCES.includes(repDistance) && text !== '') {
      setText('');
    }
  }, [repDistance]); // eslint-disable-line

  const handleChange = (e) => {
    const raw = e.target.value;
    setText(raw);
    // Live-commit: update repDistance immediately when input is valid
    const v = parseInt(raw, 10);
    if (!isNaN(v) && v >= 50 && v <= 5000) {
      setRepDistance(v);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '4px',
      padding: '4px 8px', borderRadius: '6px',
      backgroundColor: isCustom ? '#ff5a1f' : '#111820',
    }}>
      <input
        type="number"
        inputMode="numeric"
        min="50"
        max="5000"
        step="10"
        placeholder="custom"
        value={text}
        onChange={handleChange}
        style={{
          width: '70px', padding: '4px 6px', borderRadius: '4px',
          border: '1px solid #333', backgroundColor: '#0b0f14',
          color: '#fff', fontSize: '12px', fontWeight: 600,
          textAlign: 'center',
        }}
      />
      <span style={{ fontSize: '11px', color: isCustom ? '#fff' : '#666' }}>m</span>
    </div>
  );
}

/* ── Interval config form (reps x distance selector) ── */
function IntervalForm({ repCount, setRepCount, repDistance, setRepDistance, intervalsLoading, onAnalyze, onCancel,
  showTiming, setShowTiming, warmupSec, setWarmupSec, restSec, setRestSec }) {
  const fmtTime = (s) => `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}`;
  return (
    <div>
      <div style={{ fontSize: '14px', color: '#fff', fontWeight: 600, marginBottom: '12px' }}>Describe your workout</div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Reps</div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[2, 3, 4, 5, 6, 8, 10].map(n => (
              <button key={n} onClick={() => setRepCount(n)}
                style={{
                  width: '36px', height: '36px', borderRadius: '6px', border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: repCount === n ? '#ff5a1f' : '#111820',
                  color: repCount === n ? '#fff' : '#93a1b1',
                }}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <div style={{ color: '#333', fontSize: '20px', alignSelf: 'flex-end', paddingBottom: '4px' }}>x</div>
        <div>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Distance</div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
            {[100, 200, 250, 400, 500, 800, 1000].map(d => (
              <button key={d} onClick={() => setRepDistance(d)}
                style={{
                  padding: '8px 12px', borderRadius: '6px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: repDistance === d ? '#ff5a1f' : '#111820',
                  color: repDistance === d ? '#fff' : '#93a1b1',
                }}>
                {d >= 1000 ? `${d/1000}km` : `${d}m`}
              </button>
            ))}
            {/* Custom distance input */}
            <CustomDistanceInput repDistance={repDistance} setRepDistance={setRepDistance} />
          </div>
        </div>
      </div>
      {/* Timing toggle */}
      <div style={{ marginBottom: '12px' }}>
        <button onClick={() => setShowTiming(!showTiming)}
          style={{ padding: '0', border: 'none', backgroundColor: 'transparent', color: '#93a1b1', fontSize: '12px', cursor: 'pointer' }}>
          {showTiming ? '\u25B2' : '\u25BC'} {showTiming ? 'Hide timing' : 'Add timing (optional)'}
        </button>
        {showTiming && (
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '10px' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Warmup</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button onClick={() => setWarmupSec(Math.max(0, warmupSec - 30))}
                  style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #333', backgroundColor: '#111820', color: '#93a1b1', fontSize: '16px', cursor: 'pointer', lineHeight: '28px', padding: 0 }}>-</button>
                <span style={{ color: '#fff', fontSize: '15px', fontWeight: 600, minWidth: '40px', textAlign: 'center' }}>{fmtTime(warmupSec)}</span>
                <button onClick={() => setWarmupSec(Math.min(600, warmupSec + 30))}
                  style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #333', backgroundColor: '#111820', color: '#93a1b1', fontSize: '16px', cursor: 'pointer', lineHeight: '28px', padding: 0 }}>+</button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Rest between reps</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button onClick={() => setRestSec(Math.max(0, restSec - 30))}
                  style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #333', backgroundColor: '#111820', color: '#93a1b1', fontSize: '16px', cursor: 'pointer', lineHeight: '28px', padding: 0 }}>-</button>
                <span style={{ color: '#fff', fontSize: '15px', fontWeight: 600, minWidth: '40px', textAlign: 'center' }}>{fmtTime(restSec)}</span>
                <button onClick={() => setRestSec(Math.min(600, restSec + 30))}
                  style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #333', backgroundColor: '#111820', color: '#93a1b1', fontSize: '16px', cursor: 'pointer', lineHeight: '28px', padding: 0 }}>+</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={onAnalyze}
          disabled={intervalsLoading}
          style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ff5a1f', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: intervalsLoading ? 0.6 : 1 }}>
          {intervalsLoading ? 'Analyzing...' : `Analyze ${repCount} x ${repDistance >= 1000 ? `${repDistance/1000}km` : `${repDistance}m`}`}
        </button>
        <button onClick={onCancel}
          style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid #333', backgroundColor: 'transparent', color: '#666', fontSize: '13px', cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default ActivityDetail;
